import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Layout } from '@/components/custom/layout'
import { getCustomerDetail } from '@/api/customer'
import { useTabNavStore } from '@/store/tab-nav-store'
import { useState } from 'react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import UserDetailPage from '@/components/user-detail-page/user-detail-page'
import { useLocation } from '@tanstack/react-router'
import OrganizationCreateModal from '@/components/organization-detail-route/organization-create-modal'
import OrganizationDetailPage from '@/components/organization-detail-route/organization-detail-page'
import { getOrganizationDetail } from '@/api/settings'
import { useAllOrganizes } from '@/hooks/use-setting-common'
import { cn } from '@/lib/utils.js'
import { viewGuard } from '@/utils/auth.js'
import { UserRole } from '@/constants/user.js'
import { throwException } from '@/exceptions/throw-exception.js'
import QUERY_KEY from '@/constants/use-query-key.js'
export const Route = createFileRoute('/_auth/customers/$customerId')({
  beforeLoad: ({ location, params }) => {
    const pathname = location.pathname
    const tabs = useTabNavStore.getState().tabs
    if (tabs.some((tab) => tab.pathname === pathname)) return

    const { customerId } = params
    useTabNavStore.getState().addTab(pathname, `Customer ${customerId}`)
  },
  component: CustomerWrapper,
})

function CustomerWrapper() {
  const { customerId } = Route.useParams()
  const pathname = useLocation().pathname
  return (
    <CustomerDetail
      key={customerId}
      customerId={customerId}
      pathname={pathname}
    />
  )
}

function CustomerDetail({ customerId, pathname }) {
  const { refetch: refetchOrganizes } = useAllOrganizes()
  const [tab, setTab] = useState('customer')
  const setTabState = useTabNavStore((state) => state.setTabState)
  const [isOpen, setIsOpen] = useState(false)
  const [refreshTimeOrganization, setRefreshTimeOrganization] = useState(
    new Date().getTime(),
  )
  const [refreshTimeUser, setRefreshTimeUser] = useState(new Date().getTime())
  const { isLoading, data: queryData } = useQuery({
    queryKey: [
      `customer_${customerId}`,
      { customerId, isReload: refreshTimeUser },
    ],
    queryFn: async () => {
      try {
        const resp = await getCustomerDetail(customerId)
        useTabNavStore.getState().updateTabTitle(pathname, resp.data?.name)
        return resp
      } catch (error) {
        throwException(error)
        return {}
      }
    },
  })

  const customer = isLoading ? null : queryData.data

  const handleCreateOrganizeSuccess = () => {
    setTab('company')
    setTabState(pathname, { isReload: true })
    refetchOrganizes()
  }

  const handleChangeTab = (value) => {
    if (!customer?.organize_id && value === 'company') {
      setIsOpen(true)
    } else {
      setTab(value)
    }
  }

  const { data: organization } = useQuery({
    queryKey: [
      QUERY_KEY.GET_DETAIL_ORGANIZATION,
      {
        organize_id: customer?.organize_id,
        refreshTime: refreshTimeOrganization,
      },
    ],
    queryFn: async () => {
      try {
        const resp = await getOrganizationDetail({ id: customer?.organize_id })
        return resp.data
      } catch (error) {
        throwException(error)
        return []
      }
    },
    enabled: Boolean(customer?.organize_id),
  })

  return (
    <Layout.Body>
      <Tabs value={tab} onValueChange={handleChangeTab}>
        <TabsList>
          <TabsTrigger
            value="company"
            className={cn(
              'w-[150px]',
              viewGuard([UserRole.ADMINISTRATOR, UserRole.SUPERVISOR])
                ? ''
                : 'hidden',
            )}
          >
            Tổ chức
          </TabsTrigger>
          <TabsTrigger value="customer" className="w-[150px]">
            Khách hàng
          </TabsTrigger>
        </TabsList>
        <TabsContent value="customer">
          <UserDetailPage
            setRefreshTime={setRefreshTimeUser}
            isCustomer={true}
            customer={customer}
            pathname={pathname}
          />
        </TabsContent>
        <TabsContent value="company">
          <OrganizationDetailPage
            setRefreshTime={setRefreshTimeOrganization}
            pathname={`/settings/organizations/${customer?.organize_id}`}
            organization={organization}
          />
        </TabsContent>
      </Tabs>
      <OrganizationCreateModal
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        customerId={customerId}
        handleCreateSuccess={handleCreateOrganizeSuccess}
      />
    </Layout.Body>
  )
}
